/* ============================================================================
   Clearview self-service tool analytics
   Theme asset. Loaded site-wide from layout/theme.liquid, because the contact
   form attribution below has to run on the contact pages, not just the tools.

   PURPOSE
   Emits a single custom event, "cv_tool_event", through Shopify.analytics.
   A custom pixel (Settings > Customer events) subscribes to it and forwards
   it to GA4 property G-73JC3ZT6P4. Consent is handled in the pixel, not here.

   DESIGN NOTES
   - This file deliberately contains NO tool logic and touches NO tool state.
     It observes the DOM the tools already produce. That keeps the tools
     free to change (for example the lens metafield migration) without
     breaking reporting, and keeps a reporting bug from breaking a tool.
   - Every milestone fires ONCE per session per tool. A visitor who changes
     ten answers produces one "start" and one "complete", not twenty.
   - It also writes a localStorage breadcrumb, cv_tool_used (7 day window), so
     forms can stamp contact[form_source] with the tool that preceded the
     enquiry. That closes the loop from tool usage to a Zoho Desk lead
     without asking the visitor for an email.

   CHANGE LOG
   2026-10-21  Added the ROI calculator (initRoiCalculator, TOOL_LABELS entry).
               Nothing else in this file changed.
   2026-10-22  Lens calculator: specialist lens links (#cv-spec-list) now fire
               exit_click, with the model as link_text.

   DO NOT put copy, colours or URLs in this file.
   ============================================================================ */
(function () {
  "use strict";

  /* Guard: this file may be referenced from more than one place during a
     migration. Binding twice would double every event. */
  if (window.__cvToolAnalyticsLoaded) return;
  window.__cvToolAnalyticsLoaded = true;

  var SS_PREFIX = "cv_tool_fired:";
  var BREADCRUMB = "cv_tool_used";

  /* ---------- storage helpers, all failure tolerant ---------- */

  function fired(key) {
    try { return sessionStorage.getItem(SS_PREFIX + key) === "1"; }
    catch (e) { return false; }
  }

  function markFired(key) {
    try { sessionStorage.setItem(SS_PREFIX + key, "1"); } catch (e) {}
  }

  /* NOTE ON STORAGE CHOICE
     The "fired once" keys above stay in sessionStorage: they exist to stop one
     visit producing twenty duplicate events, and should reset on a new visit.
     The breadcrumb below uses localStorage instead, because a machine vision
     enquiry typically follows the tool by days, not minutes. A session-scoped
     breadcrumb would miss most real attributions. */

  function breadcrumb(tool, detail) {
    try {
      localStorage.setItem(BREADCRUMB, JSON.stringify({
        tool: tool,
        detail: detail || "",
        at: new Date().toISOString()
      }));
    } catch (e) {}
  }

  /* ---------- emit ---------- */

  function emit(tool, action, params, onceKey) {
    if (onceKey) {
      if (fired(onceKey)) return;
      markFired(onceKey);
    }
    var payload = {
      tool: tool,
      action: action,
      locale: (document.documentElement.lang || "en").toLowerCase(),
      page_path: window.location.pathname
    };
    Object.keys(params || {}).forEach(function (k) { payload[k] = params[k]; });

    try {
      if (window.Shopify && window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === "function") {
        window.Shopify.analytics.publish("cv_tool_event", payload);
      }
    } catch (e) {}
  }

  /* Fire after the tool's own click handler has updated the DOM. */
  function afterPaint(fn) { setTimeout(fn, 0); }

  /* ==========================================================================
     ILLUMINATION SELECTOR
     Root: [data-cv-illum]. Six questions, each a .cv-pills[data-q] group
     containing .cv-pill[data-v] buttons that carry aria-pressed.
     ========================================================================== */

  function initIllumination() {
    var root = document.querySelector("[data-cv-illum]");
    if (!root) return;

    var TOOL = "illumination_selector";
    var TOTAL_QUESTIONS = 6;
    var qwrap = document.getElementById("cv-questions");
    var results = document.getElementById("cv-results");

    function readAnswers() {
      var out = {};
      root.querySelectorAll(".cv-pills[data-q]").forEach(function (group) {
        var on = group.querySelector('.cv-pill[aria-pressed="true"]');
        if (on) out[group.getAttribute("data-q")] = on.getAttribute("data-v");
      });
      return out;
    }

        function topTechnique() {
      if (!results) return "";
      /* Prefer a stable key over display text. The h3 is what is on screen, so
         a browser-translated visit recorded "同軸（DOAL）" as its own value and
         the dimension fragments by language. Falls back to the h3 if the card
         carries no key, so this can only improve on the old behaviour. */
      var rec = results.querySelector(".cv-rec");
      var key = rec ? (rec.getAttribute("data-tech") || rec.getAttribute("data-key") || "") : "";
      if (key) return key;
      var h = results.querySelector(".cv-rec .cv-rectop h3");
      return h ? h.textContent.trim() : "";
    }

    if (qwrap) {
      qwrap.addEventListener("click", function (ev) {
        if (!ev.target.closest(".cv-pill")) return;

        afterPaint(function () {
          var answers = readAnswers();
          var n = Object.keys(answers).length;
          if (n === 0) return;

          emit(TOOL, "start", { first_answer: answers.task || "" }, TOOL + ":start");

          emit(TOOL, "progress", {
            steps_completed: n,
            steps_total: TOTAL_QUESTIONS
          }, TOOL + ":progress:" + n);

          if (n === TOTAL_QUESTIONS) {
            var technique = topTechnique();
            emit(TOOL, "complete", {
              task: answers.task || "",
              surface: answers.surface || "",
              presentation: answers.presentation || "",
              motion: answers.motion || "",
              mounting: answers.mounting || "",
              ambient: answers.ambient || "",
              top_technique: technique
            }, TOOL + ":complete");
            breadcrumb(TOOL, technique);
          }
        });
      });
    }

    /* Onward clicks: recommended products, shop-all family links, and the
       family links on the technique explorer cards. */
    root.addEventListener("click", function (ev) {
      var a = ev.target.closest(".cv-prodrow a, .cv-stockall");
      if (!a) return;

      var kind = a.classList.contains("cv-prodall") ? "collection"
               : a.classList.contains("cv-stockall") ? "explorer_collection"
               : "product";

      emit(TOOL, "exit_click", {
        destination_type: kind,
        destination_path: (a.getAttribute("href") || "").split("?")[0],
        link_text: (a.textContent || "").trim().slice(0, 80)
      });
      breadcrumb(TOOL, topTechnique());
    });

    /* Colour contrast simulator is a distinct tool sharing the page. Counting
       it inside the selector funnel would overstate selector completion. */
    ["cv-sw-feature", "cv-sw-background"].forEach(function (id) {
      var wrap = document.getElementById(id);
      if (!wrap) return;
      wrap.addEventListener("click", function (ev) {
        if (!ev.target.closest(".cv-swatch")) return;
        emit(TOOL, "simulator_used", {}, TOOL + ":simulator");
      });
      var picker = wrap.querySelector('input[type="color"]');
      if (picker) picker.addEventListener("input", function () {
        emit(TOOL, "simulator_used", {}, TOOL + ":simulator");
      });
    });
  }

  /* ==========================================================================
     LENS CALCULATOR
     Root: .cv-lens. calculate() runs on page load with defaults and again on
     every keystroke, so a rendered result is NOT a usage signal. Start is a
     deliberate interaction; complete is debounced so one session of typing
     produces one event.
     ========================================================================== */

  function initLensCalculator() {
    var root = document.querySelector(".cv-lens");
    if (!root) return;

       var TOOL = "lens_calculator";
    var DEBOUNCE_MS = 1500;
    var timer = null;
    var engaged = false;

    /* The values these inputs hold at page load are the factory defaults. A
       "complete" still carrying them is not a use of the tool, it is someone
       who clicked a toggle and left. In the first month 17 of 30 completes
       carried the default 23.3mm, overstating real usage by roughly half, so
       complete now requires at least one value to have actually changed. */
    var INPUT_IDS = ["cv-wd", "cv-ow", "cv-oh", "cv-fl", "cv-aov", "cv-fn", "cv-ext"];
    var initialValues = {};
    var forcedDirty = false;

    INPUT_IDS.forEach(function (id) {
      var el = document.getElementById(id);
      initialValues[id] = el ? el.value : null;
    });

    /* Picking a camera or sensor rewrites resolution and pixel size. That is a
       real specification even though the typed inputs are untouched. */
    function markDirty() { forcedDirty = true; }

    function inputsChanged() {
      if (forcedDirty) return true;
      return INPUT_IDS.some(function (id) {
        var el = document.getElementById(id);
        return el && initialValues[id] !== null && el.value !== initialValues[id];
      });
    }

    function focalOut() {
      var el = document.getElementById("cv-focal-out");
      return el ? el.textContent.trim() : "";
    }

    function sensorFormat() {
      var el = document.getElementById("cv-ro-fmt");
      return el ? el.textContent.trim() : "";
    }

    function currentMode() {
      var on = root.querySelector("#cv-toggle .cvl-active");
      return on ? on.getAttribute("data-mode") || "" : "";
    }

    function currentSource() {
      var on = root.querySelector("#cv-src-toggle .cvl-active");
      return on ? on.getAttribute("data-src") || "" : "";
    }

    function engage(trigger) {
      engaged = true;
            emit(TOOL, "start", { trigger: trigger }, TOOL + ":start");

      /* Only a changed input earns a complete. A toggle click on its own is a
         start and nothing more. */
      if (!inputsChanged()) return;

      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        var focal = focalOut();
        emit(TOOL, "complete", {
          source_mode: currentSource(),
          specify_by: currentMode(),
          sensor_format: sensorFormat(),
          focal_length_mm: focal
        }, TOOL + ":complete");
        breadcrumb(TOOL, focal ? focal + "mm" : "");
      }, DEBOUNCE_MS);
    }

    /* Deliberate interactions only. Page-load defaults never reach here. */
    root.addEventListener("click", function (ev) {
      if (ev.target.closest("#cv-src-toggle button")) return afterPaint(function () { engage("source_toggle"); });
      if (ev.target.closest("#cv-toggle button")) return afterPaint(function () { engage("mode_toggle"); });
       if (ev.target.closest("#cv-camera-list")) return afterPaint(function () { markDirty(); engage("camera_picked"); });
      if (ev.target.closest("#cv-sensor-list")) return afterPaint(function () { markDirty(); engage("sensor_picked"); });
      if (ev.target.closest("#cv-more")) {
        emit(TOOL, "show_more", {}, TOOL + ":show_more");
        return;
      }
    });

    ["cv-wd", "cv-ow", "cv-oh", "cv-fl", "cv-aov", "cv-fn", "cv-ext"].forEach(function (id) {
      var el = document.getElementById(id);
      if (!el) return;
      el.addEventListener("input", function () { engage("input:" + id); });
    });

    /* Every lens card CTA reads "View product", so the CTA's own text says
       nothing in reporting. The model sits in .cvl-lcard-name on the parent
       .cvl-lcard, so read that instead.

       Deliberately scoped to cards. The .cvl-crosslinks and .cvl-cta-row
       anchors are not cards, their own text is already descriptive, and
       closest() returns null for them so they keep the old behaviour.
       The fallback also covers a card whose markup has changed, so this
       degrades to the previous value rather than recording a blank.

       Specialist lenses (zoom, telecentric, varifocal) are plain links in
       #cv-spec-list, not cards. Each link is the model name followed by a
       <span class="cvl-tag"> with the type, so the full text would read
       "LMZ69Mzoom". Record the model only. Added 22 October 2026: before
       this, specialist lens clicks were not tracked at all. */
    function exitLinkText(a) {
      if (a.closest("#cv-spec-list")) {
        var first = a.firstChild;
        var specModel = first && first.nodeType === 3 ? (first.nodeValue || "").trim() : "";
        return specModel || (a.textContent || "").trim();
      }
      var card = a.closest(".cvl-lcard");
      var nameEl = card ? card.querySelector(".cvl-lcard-name") : null;
      var model = nameEl ? (nameEl.textContent || "").trim() : "";
      return model || (a.textContent || "").trim();
    }

    /* The highest value signal: leaving the tool for a lens product page. */
    root.addEventListener("click", function (ev) {
      var a = ev.target.closest(".cvl-lcard-cta, #cv-spec-list a, .cvl-crosslinks a, .cvl-cta-row a");
      if (!a) return;
      emit(TOOL, "exit_click", {
        destination_path: (a.getAttribute("href") || "").split("?")[0],
        link_text: exitLinkText(a).slice(0, 80),
        focal_length_mm: engaged ? focalOut() : ""
      });
      breadcrumb(TOOL, focalOut());
    });
  }

  /* ==========================================================================
     ROI CALCULATOR
     Root: [data-cv-roi]. The tool renders a result on page load with Allan's
     defaults, so a rendered result is NOT a usage signal (same lesson as the
     lens calculator). The tool publishes stable keys on its root for us to
     read: data-roi-payback (months, or "none"), data-roi-lever (labour,
     escapes, scrap, throughput, compliance) and data-roi-currency. It also
     dispatches a "cv-roi:pdf" event when a PDF is built.
     ========================================================================== */

  function initRoiCalculator() {
    var root = document.querySelector("[data-cv-roi]");
    if (!root) return;

    var TOOL = "roi_calculator";
    var DEBOUNCE_MS = 1500;
    var timer = null;

    function payback() { return root.getAttribute("data-roi-payback") || ""; }
    function lever() { return root.getAttribute("data-roi-lever") || ""; }
    function currency() { return root.getAttribute("data-roi-currency") || ""; }

    /* Only typed changes count. Defaults never reach here. */
    root.addEventListener("input", function (ev) {
      if (!ev.target.closest(".cv-roi-inputs")) return;
      emit(TOOL, "start", { trigger: "input:" + (ev.target.id || "") }, TOOL + ":start");

      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        emit(TOOL, "complete", {
          payback_months: payback(),
          top_lever: lever(),
          currency: currency()
        }, TOOL + ":complete");
        breadcrumb(TOOL, payback() === "none" ? "" : payback() + " months");
      }, DEBOUNCE_MS);
    });

    /* The lead. Fires on submit; native validation has already passed. */
    root.addEventListener("submit", function (ev) {
      if (!ev.target.matches || !ev.target.matches("[data-cv-roi-form]")) return;
      emit(TOOL, "lead_submit", {
        payback_months: payback(),
        top_lever: lever(),
        currency: currency()
      }, TOOL + ":lead_submit");
    }, true);

    /* PDF built, after the form post or from the download again button. */
    root.addEventListener("cv-roi:pdf", function () {
      emit(TOOL, "pdf_download", {
        payback_months: payback(),
        top_lever: lever()
      });
    });

    /* Onward clicks from the CTA band. */
    root.addEventListener("click", function (ev) {
      var a = ev.target.closest(".cv-roi-ctaband a");
      if (!a) return;
      emit(TOOL, "exit_click", {
        destination_path: (a.getAttribute("href") || "").split("?")[0],
        link_text: (a.textContent || "").trim().slice(0, 80)
      });
      breadcrumb(TOOL, payback() === "none" ? "" : payback() + " months");
    });
  }


  /* ==========================================================================
     CONTACT FORM ATTRIBUTION
     Appends the tool breadcrumb to the EXISTING contact[form_source] hidden
     field on any contact form in the site.

     WHY APPEND RATHER THAN ADD A FIELD
     The Zoho "Shopify Ticket To CRM Lead" Deluge function reads each field's
     value up to the next RECOGNISED label. A new hidden field would render in
     the notification email as an unrecognised label, the preceding field would
     swallow the rest of the message, and lead creation would fail with an
     INVALID_DATA length error on Company. Appending introduces no new label,
     so the parser is untouched.

     Checked 2026-08-24: no Zoho Desk or CRM rule matches Form Source on an
     exact value, so lengthening it is safe. If that ever changes, this is the
     thing that breaks.
     ========================================================================== */

  var TOOL_LABELS = {
    lens_calculator: "Lens Calculator",
    illumination_selector: "Illumination Selector",
    roi_calculator: "ROI Calculator"
  };

  /* 7 days. Ash's call, 2026-08-24. Longer catches more of the real buying
     cycle but risks tagging an unrelated later enquiry; 7 deliberately
     under-claims rather than over-claims. Change here, nowhere else. */
  var BREADCRUMB_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

  function readBreadcrumb() {
    var raw;
    try { raw = localStorage.getItem(BREADCRUMB); } catch (e) { return null; }
    if (!raw) return null;

    var obj;
    try { obj = JSON.parse(raw); } catch (e) { return null; }
    if (!obj || !obj.tool) return null;

    /* Stale breadcrumbs would misattribute an unrelated enquiry made later in
       the same session, so they are ignored rather than trusted. */
    var age = Date.now() - Date.parse(obj.at || "");
    if (!isFinite(age) || age < 0 || age > BREADCRUMB_MAX_AGE_MS) return null;

    return obj;
  }

  /* Colons are how the Deluge parser recognises a label, so they are stripped.
     Newlines and pipes are stripped for the same reason. Length is capped so a
     long value cannot push the email past a field limit. */
  function sanitise(s) {
    return String(s || "")
      .replace(/[:|\r\n\t]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 60);
  }

  function initFormStamp() {
    var fields = document.querySelectorAll('input[name="contact[form_source]"]');
    if (!fields.length) return;

    var crumb = readBreadcrumb();
    if (!crumb) return;

    var label = TOOL_LABELS[crumb.tool] || sanitise(crumb.tool);
    var detail = sanitise(crumb.detail);
    var suffix = detail ? label + " " + detail : label;

    fields.forEach(function (f) {
      var base = f.value || "";
      if (base.indexOf(suffix) !== -1) return;
      /* The ROI calculator's own form already says "ROI Calculator". Do not
         stamp it with its own breadcrumb. Other tools' crumbs still apply. */
      if (crumb.tool === "roi_calculator" && f.closest("[data-cv-roi]")) return;
      var stamped = sanitise(base) + " | " + suffix;

      /* Set BOTH the property and the attribute. The property is what gets
         submitted; the attribute is what shows in View Source and in dev
         tools. Setting only the property works, but leaves anyone inspecting
         the field convinced the stamping is broken. */
      f.value = stamped;
      f.setAttribute("value", stamped);
    });
  }

  /* ---------- boot ---------- */

  function boot() {
    try { initIllumination(); } catch (e) {}
    try { initLensCalculator(); } catch (e) {}
    try { initRoiCalculator(); } catch (e) {}
    try { initFormStamp(); } catch (e) {}
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();